// Mini temporizador e barrinha (0.67, texto 05 A1/A2). Usado no cartão da tarefa com pausas do My Planner
// e (Parte G3) na sessão de estudo. Só visual: não é tocável.
import { useEffect, useState } from 'react';
import { Text, View } from 'react-native';
import { estadoAgora, partesDaBarra, type EstadoTrecho, type Trecho } from '../lib/temporizador';
import { C, alpha } from './theme';
import { t } from '../lib/i18n';

/** Textos que a sessão de estudo troca (ex.: "📐 Matemática", "troca em 20 min", "matéria 1 de 3"). */
export type TextosMini = {
  emojiAtiv?: string;                                          // padrão 📖 (o trecho pode ter o seu: Trecho.emoji)
  nomeAtiv?: string;                                           // padrão "Em atividade" (o trecho pode ter o seu: Trecho.nome)
  proxima?: (min: number, e: EstadoTrecho) => string;          // padrão "pausa em X min"
  termina?: (min: number, e: EstadoTrecho) => string;          // último trecho: padrão "termina em X min"
  volta?: (min: number, e: EstadoTrecho) => string;            // na pausa: padrão "volta em X min"
  contaAtiv?: (e: EstadoTrecho) => string | null;              // padrão "atividade 2 de 7"
  contaPausa?: (e: EstadoTrecho) => string | null;             // padrão "pausa 2 de 6" (null = não mostra)
};

type Props = {
  trechos: Trecho[]; inicioMs: number; pausadoMs?: number;
  cor: string; emojiPausa: string; textos?: TextosMini;
  agoraMs?: number;     // opcional: hora "agora" de quem chama (senão o próprio componente conta)
};

/** Linha do mini temporizador e, embaixo, a barrinha da tarefa inteira com ▼ no momento atual. */
export function MiniTemporizador({ trechos, inicioMs, pausadoMs = 0, cor, emojiPausa, textos = {}, agoraMs }: Props) {
  const [agora, setAgora] = useState(Date.now());
  useEffect(() => { const i = setInterval(() => setAgora(Date.now()), 15000); return () => clearInterval(i); }, []);
  const ms = Math.max(agora, agoraMs ?? 0);
  const e = estadoAgora(inicioMs, trechos, ms, pausadoMs);
  if (!e.trecho) return null;
  const x = e.trecho;
  let linha: string;
  if (x.tipo === 'ativ') {
    const rotulo = `${x.emoji ?? textos.emojiAtiv ?? '📖'} ${x.nome ?? textos.nomeAtiv ?? t('Em atividade')}`;
    const prox = e.ultimo ? (textos.termina?.(e.faltaMin, e) ?? t('termina em {n} min', { n: e.faltaMin }))
      : (textos.proxima?.(e.faltaMin, e) ?? t('pausa em {n} min', { n: e.faltaMin }));
    const conta = textos.contaAtiv ? textos.contaAtiv(e) : t('atividade {n} de {total}', { n: e.nAtiv, total: e.totalAtiv });
    linha = [t('{rotulo}: {feito} min / {total}', { rotulo, feito: e.feitoMin, total: x.min }), prox, conta].filter(Boolean).join(' · ');
  } else {
    const rotulo = `${x.emoji ?? emojiPausa} ${t('Em pausa')}`;
    const volta = textos.volta?.(e.faltaMin, e) ?? (e.ultimo ? t('termina em {n} min', { n: e.faltaMin }) : t('volta em {n} min', { n: e.faltaMin }));
    const conta = textos.contaPausa ? textos.contaPausa(e) : t('pausa {n} de {total}', { n: e.nPausa, total: e.totalPausa });
    linha = [t('{rotulo}: {feito} min / {total}', { rotulo, feito: e.feitoMin, total: x.min }), volta, conta].filter(Boolean).join(' · ');
  }
  const total = e.fimMs - e.inicioMs - pausadoMs || 1;
  const pos = Math.max(0, Math.min(1, (ms - e.inicioMs - pausadoMs) / total));
  return (
    <View pointerEvents="none" style={{ gap: 2 }}>
      <Text style={{ color: x.tipo === 'ativ' ? cor : C.sub, fontSize: 12, fontWeight: '600' }} numberOfLines={2}>{linha}</Text>
      <BarraTrechos trechos={trechos} cor={cor} pos={pos} />
    </View>
  );
}

/** Barrinha fina: atividade na cor cheia, pausas em cor clara, ▼ no momento atual (pos de 0 a 1). */
export function BarraTrechos({ trechos, cor, pos }: { trechos: Trecho[]; cor: string; pos: number }) {
  const partes = partesDaBarra(trechos);
  return (
    <View pointerEvents="none" style={{ paddingTop: 9 }}>
      <View style={{ flexDirection: 'row', height: 5, borderRadius: 3, overflow: 'hidden', gap: 1 }}>
        {partes.map((p, i) => (
          <View key={i} style={{ flex: Math.max(0.0001, p.ate - p.de), backgroundColor: p.tipo === 'ativ' ? cor : alpha(cor, 0.3) }} />
        ))}
      </View>
      <View style={{ position: 'absolute', top: 0, left: `${pos * 100}%`, marginLeft: -4 }}>
        <Text style={{ color: C.text, fontSize: 8, lineHeight: 9 }}>▼</Text>
      </View>
    </View>
  );
}
